import React, { ReactNode } from 'react';
import styled from '@emotion/styled';
import { Widget, WidgetType } from '../apiService';
import { headerHeight } from '../Header';
import HtmlParser from '../HtmlParser';
import { Flex, Icon, Text } from '../styles';

// Widgets that go under the transparent header at the top of the page, so the title/body can sit on them.
const coverWidgetTypes: WidgetType[] = ['teaser_video', 'main_slider'];

const COVER_HEIGHT = 'min(calc(100vw / 1.4), 38rem)';

/** Whether the page can use its compact header: its first widget is a cover to put the title/body on. */
export const canShowCompactHeader = (widgets?: Widget[]) => {
  const first = widgets?.[0];
  return !!first && coverWidgetTypes.includes(first.content_category_type || first.type);
};

type CompactHeaderProps = {
  title?: string;
  body?: string;
  icon?: ReactNode;
};

// The page title/body in compact mode (`compact_header`), on the first widget. Rendered right before the
// WidgetManager, which gets `headerInfo={{ compact: true }}` so that widget goes under the header.
export default function CompactHeader({ title, body, icon }: CompactHeaderProps) {
  return (
    <Anchor>
      <Cover>
        <Info>
          <Flex align="center" mb={3}>
            {icon && (
              <Icon size="medium" ml={1}>
                {icon}
              </Icon>
            )}
            <Text variant="h2" weight="medium">
              {title}
            </Text>
          </Flex>
          {body ? <div className="html-summary"><HtmlParser data={body} /></div> : ''}
        </Info>
      </Cover>
    </Anchor>
  );
}

// Takes no room where the widgets start. A <header>, not a <div>, so that WidgetWrapper's `:first-of-type`
// still matches the first widget. Raised above that widget (the teaser is at zIndex 1, see TeaserVideo),
// but lets the pointer through to it except on links.
const Anchor = styled.header(() => ({
  position: 'relative',
  zIndex: 2,
  height: 0,
  pointerEvents: 'none',
  a: { pointerEvents: 'auto' },
}));

// The teaser's cover, from the top of the page under the header.
const Cover = styled.div(({ theme }) => ({
  position: 'absolute',
  top: `calc(-1 * (${headerHeight} + ${theme.spacing(3)}))`,
  right: 0,
  left: 0,
  height: COVER_HEIGHT,
}));

// Where the teaser's cover has always put the title: below md just under the cover, pulled back onto it;
// on md at its bottom; from lg in its middle.
const Info = styled.div(({ theme }) => ({
  position: 'absolute',
  right: 0,
  left: 0,
  top: 'calc(100% - 1.75rem)',
  [theme.breakpoints.up('sm')]: { top: 'calc(100% - 16rem)' },
  [theme.breakpoints.up('md')]: { top: 'auto', bottom: 0 },
  [theme.breakpoints.up('lg')]: { bottom: '50%', transform: 'translateY(50%)' },
  '@media (max-width: 600px)': {
    '> .html-summary': {
      display: 'none',
    },
  },
}));
